import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * The way out of a blocking consent surface.
 *
 * `TermsOfServiceModal` cannot be dismissed (no close button, escape and
 * outside-click both prevented) and `TosConsentGate` renders its fallback
 * *instead of* the app, so a user whose acceptance keeps failing — or whose
 * profile will not load — is otherwise locked out of the product with only a
 * toast. Signing out costs them nothing they can still reach anyway.
 *
 * `AuthStore.logout` never rejects and clears the local session before the
 * request, so the navigation below always lands on a signed-out /login.
 */
export function SignOutEscapeButton({
  variant = "ghost",
}: {
  variant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const { t } = useLanguage();
  const authStore = useAuthStore();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    await authStore.logout();
    navigate("/login", { replace: true });
  }, [authStore, navigate]);

  return (
    <Button
      data-testid="tos-sign-out"
      variant={variant}
      onClick={handleSignOut}
      disabled={signingOut}
    >
      {t.auth.tosSignOut}
    </Button>
  );
}
